import React, { useEffect, useState } from "react";
import "../ExerciseTestStepper/ExerciseTestStepper.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/logo/header-logo.svg";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { LightButton, PrimaryButton } from "../AllButtons/AllButtons";
import CongratulationsDialog from "../CongratulationsDialog/CongratulationsDialog";
import { useAppDispatch, useAppSelector } from "../../Store/store";
import { submitLessonTest, getTaskStatus, startCourse } from "../../Services/course.service";
import MathRenderer from "../CommonElements/MathRenderer";
import { openSnackbar } from "../../Slice/snackbarSlice";

const ExerciseTestCourse: React.FC = () => {
  const [disableButton, setDisableButton] = useState(false);
  const course = useAppSelector(state => state.course.course);
  const intermediate_course = useAppSelector(state => state.course?.intermediateCourse);
  const advanced_course = useAppSelector(state => state.course?.advancedCourse);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lessonIndexLocal, setLessonIndexLocal] = useState<number | null>(null);
  const [isIntermediate, setIsIntermediate] = useState(false);
  const [isAdvanced, setIsAdvanced] = useState(false);

  useEffect(() => {
    const state = location.state;
    if (state) {
      const idx = typeof state.lessonIndex === 'string' ? parseInt(state.lessonIndex) : state.lessonIndex;
      setLessonIndexLocal(idx);
      setIsIntermediate(state.intermediateCourse === 'true' || state.intermediateCourse === true);
      setIsAdvanced(state.advanceCourse === 'true' || state.advanceCourse === true);
      
      // Persist to localStorage for reload
      localStorage.setItem('exercise_lessonIndex', idx.toString());
      localStorage.setItem('exercise_isIntermediate', (state.intermediateCourse === 'true' || state.intermediateCourse === true).toString());
      localStorage.setItem('exercise_isAdvanced', (state.advanceCourse === 'true' || state.advanceCourse === true).toString());

      if (state.intermediateCourse === 'true' || state.intermediateCourse === true) {
        localStorage.setItem('intermediatenavigate', 'true');
      } else if (state.advanceCourse === 'true' || state.advanceCourse === true) {
        localStorage.setItem('advancednavigation', 'true');
      }
    } else {
      // Try to recover from localStorage
      const savedIdx = localStorage.getItem('exercise_lessonIndex');
      const savedIntermediate = localStorage.getItem('exercise_isIntermediate');
      const savedAdvanced = localStorage.getItem('exercise_isAdvanced');
      
      if (savedIdx !== null) setLessonIndexLocal(parseInt(savedIdx));
      if (savedIntermediate !== null) setIsIntermediate(savedIntermediate === 'true');
      if (savedAdvanced !== null) setIsAdvanced(savedAdvanced === 'true');
    }
  }, [location]);

  useEffect(() => {
    // If Redux state is empty, fetch data based on the course type
    if (isIntermediate && !intermediate_course) {
      const taskId = localStorage.getItem('intermediatetaskid');
      if (taskId) dispatch(getTaskStatus(taskId));
    } else if (isAdvanced && !advanced_course) {
      const taskId = localStorage.getItem('advancetaskid');
      if (taskId) dispatch(getTaskStatus(taskId));
    } else if (!isIntermediate && !isAdvanced && !course) {
      const taskId = localStorage.getItem('taskId');
      if (taskId) dispatch(getTaskStatus(taskId));
    }
  }, [isIntermediate, isAdvanced, intermediate_course, advanced_course, course, dispatch]);

  const mcqtype = () => {
    if (lessonIndexLocal === null) return null;
    
    if (isIntermediate) {
      return intermediate_course?.course_content?.[0]?.sub_lessons?.[lessonIndexLocal]?.lesson_test;
    } else if (isAdvanced) {
      return advanced_course?.course_content?.[0]?.sub_lessons?.[lessonIndexLocal]?.lesson_test;
    } else {
      return course?.course_content?.[0]?.sub_lessons?.[lessonIndexLocal]?.lesson_test;
    }
  };
  const mcq = mcqtype();
  const questions = mcq?.questions || [];
  // const selectedCourse=intermediateCourse ? intermediate_course : course;
  const [currentStep, setCurrentStep] = useState(1);
  // Store both the full option text for display and the option key for submission
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, {
    displayText: string;
    optionKey: string;
  }>>({});
  const [showCongrats, setShowCongrats] = useState(false);
  const [stepError, setStepError] = useState(false);
  
  const [requestBody, setRequestBody] = useState({
    user_id: localStorage.getItem('id'),
    submissions: [] as Array<{
      lesson_id: number;
      question_id: number;
      selected_option: string;
    }>
  });
  
  const totalQuestions = questions.length;

  const updateProgressBar = () => {
    const progressPercentage = ((currentStep - 1) / (totalQuestions - 1)) * 100;
    document
      .querySelector(".progress-bar")!
      .setAttribute("style", `width: ${progressPercentage}%`);
  };

  const selectedAnswersRef = React.useRef(selectedAnswers);
  const isSubmittingRef = React.useRef(isSubmitting);
  const isIntermediateRef = React.useRef(isIntermediate);
  const isAdvancedRef = React.useRef(isAdvanced);

  useEffect(() => { selectedAnswersRef.current = selectedAnswers; }, [selectedAnswers]);
  useEffect(() => { isSubmittingRef.current = isSubmitting; }, [isSubmitting]);
  useEffect(() => { isIntermediateRef.current = isIntermediate; }, [isIntermediate]);
  useEffect(() => { isAdvancedRef.current = isAdvanced; }, [isAdvanced]);

  useEffect(() => {
    return () => {
      const hasAnswered = Object.keys(selectedAnswersRef.current).length > 0;
      console.log("Unmount cleanup: hasAnswered =", hasAnswered, "isSubmitting =", isSubmittingRef.current);
      if (hasAnswered && !isSubmittingRef.current) {
        const currentTaskId = isIntermediateRef.current 
          ? localStorage.getItem('intermediatetaskid') 
          : isAdvancedRef.current 
            ? localStorage.getItem('advancetaskid') 
            : localStorage.getItem('taskId');
            
        console.log("Resolved currentTaskId for start-course API:", currentTaskId);
        if (currentTaskId) {
          dispatch(startCourse(currentTaskId));
        }
      }
    };
  }, []);

  const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fullOptionText = event.target.value;
    // Extract just the option key (A, B, C, D)
    const optionKey = fullOptionText.split(":")[0].trim();
    
    // Update UI state with both values
    setSelectedAnswers({
      ...selectedAnswers,
      [currentStep]: {
        displayText: fullOptionText,
        optionKey: optionKey
      }
    });
    
    // Extract the current question data
    const questionData = questions[currentStep - 1];
    
    // Update the request body
    const updatedSubmissions = [...requestBody.submissions];
    
    // Check if we already have an entry for this question
    const existingIndex = updatedSubmissions.findIndex(
      submission => submission.question_id === questionData.question_id
    );
    
    if (existingIndex >= 0) {
      // Update existing entry
      updatedSubmissions[existingIndex].selected_option = optionKey;
    } else {
      // Add new entry
      updatedSubmissions.push({
        lesson_id: questionData.lesson_id,
        question_id: questionData.question_id,
        selected_option: optionKey
      });
    }
    
    setRequestBody({
      ...requestBody,
      submissions: updatedSubmissions
    });
    setStepError(false);
  };

  // const displayStep = (stepNumber: number) => {
  //   if (stepNumber >= 1 && stepNumber <= totalQuestions) {
  //     setCurrentStep(stepNumber);
  //     updateProgressBar();
  //   }
  // };

  const nextStep = () => {
    if (selectedAnswers[currentStep] && selectedAnswers[currentStep].optionKey) {
      setStepError(false);
      if (currentStep < totalQuestions) {
        setCurrentStep((prev) => prev + 1);
        updateProgressBar();
      }
    } else {
      setStepError(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      updateProgressBar();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(selectedAnswers[currentStep] && selectedAnswers[currentStep].optionKey)) {
      setStepError(true);
      return;
    }
    setStepError(false);
    setIsSubmitting(true);
    setDisableButton(true);
    setShowCongrats(true);
    try {
      await dispatch(submitLessonTest(requestBody)).unwrap();
    } catch (error) {
      console.error("Error submitting lesson test:", error);
      setIsSubmitting(false);
      setDisableButton(false);
      setShowCongrats(false);
      dispatch(
        openSnackbar({
          message: "Something went wrong. Please try again",
          severity: "error",
        }),
      );
    }
  };

  const progressPercentage = showCongrats
    ? 100
    : Math.floor(((currentStep - 1) / totalQuestions) * 100);

  return (
    <>
      <div className="exercise-test-section">
        <div className="test-stepper-wrapper">
          <div className="test-stepper-header">
            <Link to="/">
              <img src={Logo} alt="" />
            </Link>
          </div>

          <div className="test-stepper-content-body">
            <div className="test-stepper-container" id="container">
              <div className="progress-label">
                <span>
                  <span>Question</span>{' '}
                  <span className="notranslate">{currentStep}</span>{' '}
                  <span>of</span>{' '}
                  <span className="notranslate">{totalQuestions}</span>
                </span>
                <span>
                  <span className="notranslate">{progressPercentage}</span>%
                </span>
              </div>
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${progressPercentage}%` }}
                  aria-valuenow={currentStep}
                  aria-valuemin={1}
                  aria-valuemax={totalQuestions}
                ></div>
              </div>

              <form id="multi-step-form" onSubmit={handleSubmit}>
                {questions.map((question: any, index: any) => {
                  // Convert options object to array for mapping
                  const optionsArray = question.options ?
                    Object.entries(question.options).map(([key, value]) => `${key}: ${value}`) :
                    [];

                  return (
                    <div
                      key={index}
                      className={`step step-${index + 1} ${currentStep === index + 1 ? "d-block" : "d-none"
                        }`}
                    >
                      <div className="mb-3">
                        <div className="stepper-form-block">
                          <FormControl>
                            <FormLabel
                              className="stepper-question"
                              id={`question-label-${index + 1}`}
                            >
                              <MathRenderer text={`${index + 1}. ${question.question}`} />
                            </FormLabel>
                            <RadioGroup
                              aria-labelledby={`question-label-${index + 1}`}
                              name={`radio-buttons-group-${index + 1}`}
                              value={selectedAnswers[currentStep]?.displayText || ""}
                              onChange={handleAnswerChange}
                            >
                              {optionsArray.map((option, optionIndex) => (
                                <FormControlLabel
                                  key={optionIndex}
                                  value={option}
                                  control={<Radio />}
                                  label={<MathRenderer text={option} />}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </div>
                      <div className="stepper-form-action">
                        <LightButton
                          onClick={() => {
                            if (currentStep > 1) {
                              prevStep();
                            } else {
                              if (isAdvanced) navigate('/dashboard/advanced_course');
                              else if (isIntermediate) navigate('/dashboard/intermediate_course');
                              else navigate('/dashboard/enrolled_course');
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="16"
                            viewBox="0 0 14 16"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_2582_54166)">
                              <path
                                d="M0.292969 7.29365C-0.0976562 7.68428 -0.0976562 8.31865 0.292969 8.70928L5.29297 13.7093C5.68359 14.0999 6.31797 14.0999 6.70859 13.7093C7.09922 13.3187 7.09922 12.6843 6.70859 12.2937L3.41172 8.9999H12.9992C13.5523 8.9999 13.9992 8.55303 13.9992 7.9999C13.9992 7.44678 13.5523 6.9999 12.9992 6.9999H3.41484L6.70547 3.70615C7.09609 3.31553 7.09609 2.68115 6.70547 2.29053C6.31484 1.8999 5.68047 1.8999 5.28984 2.29053L0.289844 7.29053L0.292969 7.29365Z"
                                fill="#4B5563"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2582_54166">
                                <path d="M0 0H14V16H0V0Z" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>{" "}
                          Previous
                        </LightButton>
                        {currentStep < totalQuestions ? (
                          <>
                            {stepError && (
                              <div className="step-error" style={{ color: 'red', marginBottom: 8 }}>
                                Please answer the question before proceeding.
                              </div>
                            )}
                            <PrimaryButton className="next-button"
                              onClick={nextStep}
                            >
                              Next
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="12"
                                viewBox="0 0 14 12"
                                fill="none"
                              >
                                <path
                                  d="M13.7063 6.70615C14.0969 6.31553 14.0969 5.68115 13.7063 5.29053L8.70625 0.290527C8.31563 -0.100098 7.68125 -0.100098 7.29063 0.290527C6.9 0.681152 6.9 1.31553 7.29063 1.70615L10.5875 4.9999H1C0.446875 4.9999 0 5.44678 0 5.9999C0 6.55303 0.446875 6.9999 1 6.9999H10.5844L7.29375 10.2937C6.90312 10.6843 6.90312 11.3187 7.29375 11.7093C7.68437 12.0999 8.31875 12.0999 8.70938 11.7093L13.7094 6.70928L13.7063 6.70615Z"
                                  fill="white"
                                />
                              </svg>
                            </PrimaryButton>
                          </>
                        ) : (
                          <>
                            {stepError && (
                              <div className="step-error" style={{ color: 'red', marginBottom: 8 }}>
                                Please answer the question before proceeding.
                              </div>
                            )}
                            <PrimaryButton className="next-button"
                              onClick={handleSubmit} disabled={disableButton}
                            >
                              Submit
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="12"
                                viewBox="0 0 14 12"
                                fill="none"
                              >
                                <path
                                  d="M13.7063 6.70615C14.0969 6.31553 14.0969 5.68115 13.7063 5.29053L8.70625 0.290527C8.31563 -0.100098 7.68125 -0.100098 7.29063 0.290527C6.9 0.681152 6.9 1.31553 7.29063 1.70615L10.5875 4.9999H1C0.446875 4.9999 0 5.44678 0 5.9999C0 6.55303 0.446875 6.9999 1 6.9999H10.5844L7.29375 10.2937C6.90312 10.6843 6.90312 11.3187 7.29375 11.7093C7.68437 12.0999 8.31875 12.0999 8.70938 11.7093L13.7094 6.70928L13.7063 6.70615Z"
                                  fill="white"
                                />
                              </svg>
                            </PrimaryButton>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </form>
            </div>
          </div>
        </div>
      </div>
      {showCongrats && <CongratulationsDialog />}
    </>
  );
};

export default ExerciseTestCourse;